/**
 * career/insights.ts — pure work-insight rules.
 *
 * Every insight derives from RECORDED career data (profile, skills, market
 * signals, income, projects) with an explicit basis. No invented market
 * wisdom — "your skills are in demand" is only possible when a sourced
 * market signal is recorded. Spec flavor examples needing external feeds
 * (high-match opportunities) arrive only via future providers.
 */

import type {
  CareerInsight,
  CareerProfile,
  CareerProject,
  CareerSkill,
  IncomeRecord,
  MarketSignal,
} from './models';
import { computeIncomeIntelligence } from './analytics';

const DAY = 24 * 60 * 60 * 1000;

export function computeInsights(input: {
  profile: CareerProfile;
  skills: CareerSkill[];
  marketSignals: MarketSignal[];
  incomeRecords: IncomeRecord[];
  projects: CareerProject[];
  now: number;
}): CareerInsight[] {
  const { profile, skills, marketSignals, incomeRecords, projects, now } = input;
  const insights: CareerInsight[] = [];
  const skillNames = new Set(skills.map((s) => s.name.toLowerCase()));
  const inDemand = marketSignals.filter((m) => m.kind === 'inDemandSkill');

  // Rate below target.
  if (profile.hourlyRate != null && profile.targetHourlyRate != null &&
      profile.hourlyRate < profile.targetHourlyRate) {
    insights.push({
      id: 'raise-rate-to-target',
      message: `Your hourly rate (${profile.currency} ${profile.hourlyRate}) is below your target (${profile.currency} ${profile.targetHourlyRate}) — plan a rate increase.`,
      basis: [`hourlyRate ${profile.hourlyRate}`, `targetHourlyRate ${profile.targetHourlyRate}`],
      severity: 'nudge',
    });
  }

  // Realized hourly average above the listed rate → the market already pays more.
  const intel = computeIncomeIntelligence(incomeRecords, profile.currency, now);
  if (intel.hourlyAverage != null && profile.hourlyRate != null &&
      intel.hourlyAverage > profile.hourlyRate * 1.15) {
    insights.push({
      id: 'raise-rate-earned-more',
      message: `Your recorded work averages ${profile.currency} ${Math.round(intel.hourlyAverage)}/h — above your listed ${profile.currency} ${profile.hourlyRate}/h. Increase your hourly rate.`,
      basis: ['recorded income with hours', `hourlyRate ${profile.hourlyRate}`],
      severity: 'important',
    });
  }

  // Recorded market demand for skills the user HAS.
  for (const signal of inDemand) {
    if (skillNames.has(signal.name.toLowerCase())) {
      insights.push({
        id: `skill-in-demand-${signal.id}`,
        message: `Your ${signal.name} skills are in demand (${signal.source}).`,
        basis: [`market signal from ${signal.source}`],
        severity: 'info',
      });
    }
  }

  // Recorded market demand for skills the user LACKS → specialization path.
  for (const signal of inDemand) {
    if (!skillNames.has(signal.name.toLowerCase())) {
      insights.push({
        id: `skill-gap-${signal.id}`,
        message: `You could earn more by adding ${signal.name} — recorded as in demand (${signal.source}).`,
        basis: [`market signal from ${signal.source}`, 'not in your skill list'],
        severity: 'nudge',
      });
    }
  }

  // Stale portfolio (recorded links, no update in 6 months).
  if (profile.portfolioLinks.length > 0 && profile.portfolioUpdatedAt != null &&
      now - profile.portfolioUpdatedAt > 180 * DAY) {
    insights.push({
      id: 'update-portfolio',
      message: 'Update your portfolio — it has not changed in over six months.',
      basis: [`portfolioUpdatedAt ${new Date(profile.portfolioUpdatedAt).toISOString().slice(0, 10)}`],
      severity: 'nudge',
    });
  }

  // Income pace vs monthly goal (current month, recorded income only).
  if (profile.monthlyIncomeGoal != null) {
    const d = new Date(now);
    const monthKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const thisMonth = intel.monthlyEarnings.find((m) => m.month === monthKey)?.total ?? 0;
    const dayOfMonth = d.getUTCDate();
    if (dayOfMonth >= 15 && thisMonth < profile.monthlyIncomeGoal * 0.4) {
      insights.push({
        id: 'income-behind-goal',
        message: `Recorded income this month (${profile.currency} ${thisMonth}) is behind your ${profile.currency} ${profile.monthlyIncomeGoal} goal — plan more paid work this week.`,
        basis: [`month-to-date ${thisMonth}`, `monthlyIncomeGoal ${profile.monthlyIncomeGoal}`],
        severity: 'important',
      });
    }
  }

  // Undecided proposals piling up.
  const pending = projects.filter((p) => p.accepted == null && p.completedAt == null);
  if (pending.length >= 3) {
    insights.push({
      id: 'pending-proposals',
      message: `${pending.length} recorded projects have no accepted/rejected outcome — update their status to keep your acceptance rate honest.`,
      basis: [`${pending.length} projects with accepted=null`],
      severity: 'info',
    });
  }

  return insights;
}
